/**
 * ViewingAsContext — superadmin "view as user" feature
 *
 * When a superadmin selects a user from the header dropdown:
 *   1. We call POST /api/admin/users/:id/login-token with the superadmin JWT
 *   2. We store the returned user-token in sessionStorage under 'token_superadmin_delegate'
 *   3. api.js reads `window.__viewingAsToken` (set here) and uses it for all requests
 *   4. The original superadmin token stays in localStorage under 'token'
 *   5. When the superadmin clears the selection, we restore normal token usage
 *
 * This means every API call (accounts, analytics, conversations, VPS stats…)
 * goes through as the selected user — exactly what that user sees — while the
 * superadmin's own JWT and role are still available for nav / Orbex Licenses.
 */

import React, { createContext, useContext, useState, useCallback } from 'react';
import { api } from '../services/api';

const ViewingAsContext = createContext(null);

export function useViewingAs() {
  return useContext(ViewingAsContext);
}

export function ViewingAsProvider({ children }) {
  const [viewingAs, setViewingAs]   = useState(null); // { _id, username, name, email, role }
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');

  /**
   * Switch to viewing as a specific user.
   * Fetches a delegate token from the backend using the superadmin JWT.
   */
  const switchToUser = useCallback(async (targetUser) => {
    if (!targetUser?._id) return;
    setLoading(true); setError('');
    try {
      // POST uses the current (superadmin) token automatically via api.js interceptor
      const res = await api.post(`/admin/users/${targetUser._id}/login-token`);
      if (!res.data?.success || !res.data?.token) {
        throw new Error(res.data?.error || 'Failed to get delegate token');
      }
      // Store delegate token so api.js picks it up
      window.__viewingAsToken = res.data.token;
      sessionStorage.setItem('wingr_viewing_as_token', res.data.token);
      sessionStorage.setItem('wingr_viewing_as_user', JSON.stringify(targetUser));
      setViewingAs(targetUser);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Clear "view as" — restore superadmin's own token.
   */
  const clearViewingAs = useCallback(() => {
    window.__viewingAsToken = null;
    sessionStorage.removeItem('wingr_viewing_as_token');
    sessionStorage.removeItem('wingr_viewing_as_user');
    setViewingAs(null);
    setError('');
  }, []);

  // On mount, restore from sessionStorage (survives page refresh)
  React.useEffect(() => {
    const savedToken = sessionStorage.getItem('wingr_viewing_as_token');
    const savedUser  = sessionStorage.getItem('wingr_viewing_as_user');
    if (savedToken && savedUser) {
      try {
        window.__viewingAsToken = savedToken;
        setViewingAs(JSON.parse(savedUser));
      } catch { /* corrupt — ignore */ }
    }
  }, []);

  return (
    <ViewingAsContext.Provider value={{ viewingAs, loading, error, switchToUser, clearViewingAs }}>
      {children}
    </ViewingAsContext.Provider>
  );
}
